import { useState } from 'react';
import { api } from '../lib/api';
import type { Page } from '../lib/api';
import type { PolicySummary } from '../lib/types';
import { useResource } from '../lib/useResource';
import { Card, Empty, Loading, Notice, StatusPill, exactly } from '../components/ui';

interface PolicyDetail {
  policy: PolicySummary;
  document: Record<string, unknown>;
  assignments: {
    id: string;
    scopeType: string;
    scopeValue: string | null;
    priority: number;
  }[];
}

export function Policies() {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const policies = useResource(() => api.get<Page<PolicySummary>>('/api/v1/policies', { size: 50 }), []);
  const detail = useResource(
    () => (selectedId ? api.get<PolicyDetail>(`/api/v1/policies/${selectedId}`) : Promise.resolve(null)),
    [selectedId],
  );

  return (
    <>
      <div className="page-head">
        <h1>Policy</h1>
        <p>
          What each device is told to enforce and collect. A policy is edited as a draft, published
          as a new version, and assigned to a scope; devices pick up changes on their next check-in.
        </p>
      </div>

      <Card title="Policies">
        {policies.loading && !policies.data && <Loading />}
        {policies.error && <Notice kind="error">{policies.error}</Notice>}
        {policies.data?.content.length === 0 && <Empty>No policies have been created.</Empty>}

        {policies.data && policies.data.content.length > 0 && (
          <table>
            <thead>
              <tr><th>Name</th><th className="num">Version</th><th>Status</th><th>Published</th><th></th></tr>
            </thead>
            <tbody>
              {policies.data.content.map((policy) => (
                <tr key={policy.id}>
                  <td>
                    <strong>{policy.name}</strong>
                    <div style={{ color: 'var(--muted)', fontSize: 12.5, maxWidth: '60ch' }}>
                      {policy.description}
                    </div>
                  </td>
                  <td className="num">{policy.version}</td>
                  <td><StatusPill value={policy.status} /></td>
                  <td>{policy.publishedAt ? exactly(policy.publishedAt) : '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="secondary small"
                      onClick={() => setSelectedId(policy.id === selectedId ? null : policy.id)}
                    >
                      {policy.id === selectedId ? 'Hide' : 'Inspect'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {selectedId && detail.data && (
        <Card title={`${detail.data.policy.name} v${detail.data.policy.version}`}>
          <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>Assignments</h3>
          {detail.data.assignments.length === 0 ? (
            <Empty>This version is not assigned to any device.</Empty>
          ) : (
            <ul style={{ margin: '0 0 16px', paddingLeft: 18, fontSize: 13 }}>
              {detail.data.assignments.map((assignment) => (
                <li key={assignment.id}>
                  {assignment.scopeType.toLowerCase()}
                  {assignment.scopeValue ? ` = ${assignment.scopeValue}` : ''} (priority {assignment.priority})
                </li>
              ))}
            </ul>
          )}

          <h3 style={{ fontSize: 13, margin: '0 0 8px' }}>Document</h3>
          <pre className="evidence">{JSON.stringify(detail.data.document, null, 2)}</pre>
        </Card>
      )}
    </>
  );
}
